import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import * as Core from '../../core';
import { Button } from '../../components/button';
import { IconButton } from '../../components/buttons-visuals';
import { ContentContainer } from '../../components/containers';
import ImageUploader from '../../components/imageUploader';
import Modal from '../../components/modal';
import { LeagueService } from '../../services/leagueService';

const byName = (a: Core.Models.LeagueComingSoonAsset, b: Core.Models.LeagueComingSoonAsset) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

const ComingSoonAssets = (): JSX.Element => {
    const [assets, setAssets] = useState<Core.Models.LeagueComingSoonAsset[]>([]);
    const [name, setName] = useState('');
    const [isDeletingId, setIsDeletingId] = useState<string | undefined>(undefined);
    const [editing, setEditing] = useState<Core.Models.LeagueComingSoonAsset | undefined>(undefined);
    const [description, setDescription] = useState('');
    const [isLaunch, setIsLaunch] = useState(true);
    const [rubricImageData, setRubricImageData] = useState<string | undefined>(undefined);
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(async () => {
        const data = await LeagueService.getComingSoonAssets();
        setAssets([...data].sort(byName));
    }, []);

    useEffect(() => {
        load().catch((e) => toast.error(Core.API.getErrorMessage(e)));
    }, [load]);

    const uploadImage = useCallback(
        async (imageData: string) => {
            const trimmed = name.trim();
            if (!trimmed) {
                toast.error('Enter a name before uploading an image.');
                throw new Error('Name is required.');
            }
            await LeagueService.addComingSoonAsset({ imageData, name: trimmed });
            setName('');
            toast.success('Game added.');
            await load();
        },
        [load, name]
    );

    const remove = useCallback(
        async (id: string) => {
            setIsDeletingId(id);
            try {
                await LeagueService.deleteComingSoonAsset(id);
                await load();
            } catch (e) {
                toast.error(Core.API.getErrorMessage(e));
            } finally {
                setIsDeletingId(undefined);
            }
        },
        [load]
    );

    const openEdit = (asset: Core.Models.LeagueComingSoonAsset) => {
        setEditing(asset);
        setDescription(asset.description || '');
        setIsLaunch(asset.isLaunch);
        setRubricImageData(undefined);
    };

    const saveEdit = async () => {
        if (!editing) return;
        setIsSaving(true);
        try {
            await LeagueService.updateComingSoonAsset({
                description,
                id: editing.id,
                isLaunch,
                ...(rubricImageData ? { rubricImageData } : {}),
            });
            toast.success('Game updated.');
            setEditing(undefined);
            await load();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    const launchGames = useMemo(() => assets.filter((asset) => asset.isLaunch), [assets]);
    const comingSoon = useMemo(() => assets.filter((asset) => !asset.isLaunch), [assets]);

    const renderAsset = (asset: Core.Models.LeagueComingSoonAsset) => (
        <li className="disp-flex align-center gap mb" key={asset.id}>
            <img alt="" src={asset.imageUrl} style={{ width: 160, height: 'auto' }} />
            <span>{asset.name}</span>
            <IconButton
                as="button"
                buttonLabel={`Edit ${asset.name}`}
                buttonSize="medium"
                onClick={() => openEdit(asset)}
            >
                <FontAwesomeIcon icon={['fas', 'edit']} />
            </IconButton>
            <IconButton
                as="button"
                buttonLabel={`Delete ${asset.name}`}
                buttonSize="medium"
                color="destructive"
                disabled={isDeletingId === asset.id}
                onClick={() => void remove(asset.id)}
            >
                <FontAwesomeIcon icon={['fas', 'trash']} />
            </IconButton>
        </li>
    );

    return (
        <ContentContainer className="global-container-centered" shade={Core.Models.Shades.Dark40}>
            <p className="mb2x">
                Add each game with a name and cover image. New games start in Launch games. Use the pencil to move a
                game to Coming soon, and to add a description and rubric.
            </p>
            <label className="disp-block mb" htmlFor="coming-soon-name">
                Name
            </label>
            <input
                className="form-control mb2x"
                id="coming-soon-name"
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                type="text"
                value={name}
            />
            <ImageUploader
                contentType="image/png"
                disableCrop
                icon="upload"
                text="Upload image"
                uploadImage={uploadImage}
            />
            <h3 className="heading-3 mt2x">Launch games</h3>
            <ul className="mb2x">{launchGames.map(renderAsset)}</ul>
            <h3 className="heading-3">Coming soon</h3>
            <ul className="mb0">{comingSoon.map(renderAsset)}</ul>
            {editing && (
                <Modal onClose={() => !isSaving && setEditing(undefined)} title={`Edit ${editing.name}`}>
                    <label className="disp-block mb" htmlFor="ncbc-game-row">
                        Row
                    </label>
                    <select
                        className="form-control mb2x"
                        id="ncbc-game-row"
                        onChange={(event) => setIsLaunch(event.target.value === 'launch')}
                        value={isLaunch ? 'launch' : 'coming-soon'}
                    >
                        <option value="launch">Launch games</option>
                        <option value="coming-soon">Coming soon</option>
                    </select>
                    <label className="disp-block mb" htmlFor="ncbc-game-description">
                        Description (markdown)
                    </label>
                    <textarea
                        className="form-control mb2x"
                        id="ncbc-game-description"
                        maxLength={Core.Constants.MARKDOWN_MAX_LENGTH}
                        onChange={(event) => setDescription(event.target.value)}
                        rows={8}
                        value={description}
                    />
                    {editing.rubricImageUrl && !rubricImageData && (
                        <img alt="" className="mb2x" src={editing.rubricImageUrl} style={{ maxWidth: '100%' }} />
                    )}
                    <ImageUploader
                        contentType="image/png"
                        disableCrop
                        icon="upload"
                        text="Upload rubric screenshot"
                        uploadImage={async (imageData: string) => {
                            setRubricImageData(imageData);
                        }}
                    />
                    <Button className="mt2x" disabled={isSaving} onClick={() => void saveEdit()} round wide>
                        {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                </Modal>
            )}
        </ContentContainer>
    );
};

export default ComingSoonAssets;
